import { Edge, Node, XYPosition } from "reactflow";
import dagre from "dagre";
import { FlowNodeType } from "./TriggerFlow";
import produce from "immer";
import { isOverlap, TypedNode } from "./reducer/stateUtils";
import { TriggerStepCreate, TriggerForEditor } from "@dots-projects/types";
import invariant from "ts-invariant";
import { createNodesAndEdges } from "./createNodeAndEdges";
import { TriggerState } from "./reducer/reducerTypes";
import {
  getRoots,
  dfsSteps,
  collectDownstreamSteps,
  getIdStepMap,
  willCollide,
  getEntryStep,
} from "./reducer/triggerUtils";

// Fixed width for every node to make the layout easier
export const NODE_WIDTH = 220;

export const nodeSizes: { [key in FlowNodeType]: { w: number; h: number } } = {
  trigger: { w: NODE_WIDTH, h: 120 },
  step: { w: NODE_WIDTH, h: 180 },
  condition: { w: NODE_WIDTH, h: 100 },
  "if-else": { w: NODE_WIDTH, h: 100 },
  placeholder: { w: NODE_WIDTH, h: 100 },
  "placeholder-published": { w: NODE_WIDTH, h: 100 },
};

/**
 *
 * Lay out the nodes using dagre. Rigth now we use a naiive approach that assume equal width of all nodes.
 *
 * @see https://reactflow.dev/docs/examples/layout/dagre/
 */
export const createLayout = (nodes: TypedNode[], edges: Edge[]) => {
  if (!nodes?.length || !edges?.length) {
    return { edges: [], nodes: [] };
  }
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({ rankdir: "TB", ranker: "tight-tree", ranksep: 20 });

  // Set nodes
  nodes.forEach((node) => {
    const sz = nodeSizes[node.type as FlowNodeType];
    dagreGraph.setNode(node.id, {
      height: sz.h,
      width: sz.w,
    });
  });

  // Set edges
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Create layout
  dagre.layout(dagreGraph);

  // Make sure the leftmost root node is at (0, 0). This will make the following layout work much easier.
  let top = Number.MAX_VALUE;
  let left = Number.MAX_VALUE;
  dagreGraph.nodes().forEach((id) => {
    const node = dagreGraph.node(id);
    if (node && top >= node.y) {
      top = node.y;
      left = node.x;
    }
  });

  // Update nodes w/new layout
  const newNodes = produce(nodes, (draft) => {
    draft.forEach((node: Node) => {
      const nodeWithPosition = dagreGraph.node(node.id);

      node.position = {
        x: nodeWithPosition.x - left,
        y: nodeWithPosition.y - top,
      };
      node.width = nodeWithPosition.width;
      node.height = nodeWithPosition.height;

      return node;
    });
  });

  return { nodes: newNodes, edges };
};

/**
 *
 * If the trigger doesn't have position information, run an intial round of layout on all nodes
 *
 * @param outputState
 */
export function initTriggerLayout(outputState: TriggerState, force = false) {
  const triggerPosition = outputState.configs?.$$editorPosition;
  const stepIdsWithoutPosition: string[] = [];
  if (
    force ||
    typeof triggerPosition?.x !== "number" ||
    typeof triggerPosition?.y !== "number"
  ) {
    const { nodes, edges } = createNodesAndEdges(outputState);
    const { nodes: nodesWithPosition } = createLayout(nodes, edges);
    copyPositionsToTrigger({ x: 0, y: 0 }, nodesWithPosition, outputState);
  } // otherwise we've run this initialization and preserved the positions in the trigger
  // Initialize the positions of steps that don't have positions to prevent crashing.
  outputState.steps.forEach((step) => {
    if (
      typeof step.configs?.$$editorPosition?.x !== "number" ||
      typeof step.configs?.$$editorPosition?.y !== "number"
    ) {
      step.configs = {
        ...step.configs,
        $$editorPosition: { x: 0, y: 200 },
        $$editorPositionWhenNode: { x: 0, y: 200 },
      };
      stepIdsWithoutPosition.push(step.id);
    }
  });
  return stepIdsWithoutPosition;
}

/**
 *
 * Copy positions in the given React Flow nodes to `outputTrigger`
 *
 * All positions are offset by `initialPosition`
 *
 * @param initialPosition
 * @param nodes nodes from which we obtain the positions
 * @param outputTrigger
 */
function copyPositionsToTrigger(
  initialPosition: XYPosition,
  nodes: TypedNode[],
  outputTrigger: TriggerForEditor
) {
  const idStepMap = outputTrigger.steps.reduce(
    (map, s) => map.set(s.id, s),
    new Map<string, TriggerStepCreate>()
  );
  const { x: offsetX, y: offsetY } = initialPosition;
  nodes.forEach((node) => {
    const { x, y } = node.position;
    switch (node.type) {
      case "trigger":
        outputTrigger.configs = {
          ...outputTrigger.configs,
          $$editorPosition: { x: x + offsetX, y: y + offsetY },
        };
        break;
      case "step":
      case "placeholder":
      case "if-else":
      case "placeholder-published": {
        const step = idStepMap.get(node.id);
        if (step) {
          step.configs = {
            ...step.configs,
            $$editorPosition: { x: x + offsetX, y: y + offsetY },
          };
        } // else it's implicit placeholder
        break;
      }
      case "condition": {
        const step = idStepMap.get(node.data.stepId);
        invariant(
          step,
          `Cannot find corresponding step for condition node! stepId=${node.data.stepId}`
        );
        // @ts-ignore; TODO fix this error
        step.configs = {
          ...step.configs,
          $$editorPositionWhenNode: { x: x + offsetX, y: y + offsetY },
        };
        break;
      }
    }
  });
}

function createNodesFromStepsWithPosition(steps: TriggerStepCreate[]) {
  if (steps.length === 0) return [];
  // What matters in templateTrigger is just `steps`. Other props are just dummy data that doesn't affect the layout algorithm. But we do need to specify them to avoid type errors.
  const templateTrigger: TriggerForEditor = {
    steps,
    type: "team_join",
    authorUserId: "1",
    communityId: "1",
    platform: "discord",
    id: "1",
    isDraft: true,
    name: "1",
    liveTriggerConfigs: { type: "button_click" },
    configs: { $$editorPosition: { x: 0, y: 0 } },
    workspace: "1",
    updatedAt: new Date(),
  };
  const { nodes: nodes0, edges } = createNodesAndEdges(templateTrigger);
  const nodes = nodes0.filter((n) => n.type !== "trigger"); // Remove trigger root
  copyPositions(templateTrigger, edges, nodes);
  return nodes;
}

/**
 * Copy the position information stored in the given trigger to `outputNodes` so that they can be passed to React Flow for display
 *
 * @param trigger the trigger to copy from
 * @param edges
 * @param outputNodes React Flow nodes to update
 */
export function copyPositions(
  trigger: TriggerForEditor,
  edges: Edge[],
  outputNodes: TypedNode[]
) {
  invariant(
    typeof trigger.configs?.$$editorPosition?.x === "number" &&
      typeof trigger.configs.$$editorPosition.y === "number",
    `The trigger doesn't have editor positions set!`
  );
  const idStepMap = trigger.steps.reduce(
    (map, s) => map.set(s.id, s),
    new Map<string, TriggerStepCreate>()
  );
  const implicitPlaceholders: TypedNode[] = [];
  // Copy positions from trigger to outputNodes
  outputNodes.forEach((node) => {
    const nodeSize = nodeSizes[node.type];
    node.width = nodeSize.w;
    node.height = nodeSize.h;
    switch (node.type) {
      case "trigger":
        node.position = trigger.configs?.$$editorPosition!; // we use ! here since we've already checked it in the invariant.
        break;
      case "step":
      case "placeholder":
      case "if-else":
      case "placeholder-published": {
        const step = idStepMap.get(node.id);
        if (step) {
          node.position = step.configs?.$$editorPosition!;
        } else {
          // it's implicit placeholder, manually position it
          invariant(
            node.type === "placeholder",
            `Invalid node type ${node.type}(${node.id})!`
          );
          implicitPlaceholders.push(node);
        }
        break;
      }
      case "condition":
        {
          const step = idStepMap.get(node.data.stepId);
          invariant(
            step,
            `Cannot find corresponding step for condition node! stepId=${node.data.stepId}`
          );
          invariant(
            step.configs?.$$editorPositionWhenNode,
            `Step doesn't have whenNode position: ${step.id}`
          );
          node.position = step.configs?.$$editorPositionWhenNode;
        }
        break;
      default:
        break;
    }
  });

  // Manually position implicit placeholders relative to their parent. This is because implicit placeholders don't have corresponding trigger steps.
  implicitPlaceholders.forEach((n) => {
    const edge = edges.find((e) => e.target === n.id);
    invariant(
      edge,
      `No edge pointing to ${n.id}. The node shouldn't have existed in the first place!`
    );
    const parentNode = outputNodes.find((n) => n.id === edge.source);
    invariant(parentNode, `Can't find source of an edge: ${edge.source}!`);
    n.position = {
      x: parentNode.position.x,
      y: parentNode.position.y + nodeSizes[parentNode.type].h,
    };
  });
}

/**
 * Lay out startingStep and all its downstream steps
 *
 * We use `createNodesAndEdges` and `createLayout` for convenience. In theory we don't have to use them and we could write our own layout algorithm.
 *
 * @param startingStep
 * @param outputSteps steps to update
 */
function layoutDownstream(
  startingStep: TriggerStepCreate | undefined,
  outputSteps: TriggerStepCreate[]
) {
  // Save the original position of the startingStep
  const startingPosition = {
    x: 0,
    y: nodeSizes["step"].h,
    ...startingStep?.configs?.$$editorPosition,
  };
  const stepsToLayout = collectDownstreamSteps(
    startingStep ?? outputSteps[0],
    outputSteps
  );
  // What matters in templateTrigger is just `steps`. Other props are just dummy data that doesn't affect the layout algorithm. But we do need to specify them to avoid type errors.
  const templateTrigger: TriggerForEditor = {
    steps: stepsToLayout,
    type: "team_join",
    authorUserId: "1",
    communityId: "1",
    platform: "discord",
    id: "1",
    isDraft: true,
    name: "1",
    liveTriggerConfigs: { type: "button_click" },
    workspace: "1",
    updatedAt: new Date(),
  };
  const { nodes: nodes0, edges } = createNodesAndEdges(templateTrigger);
  const nodes = nodes0.filter((n) => n.type !== "trigger"); // Remove trigger root
  const { nodes: layedOutNodes } = createLayout(nodes, edges);

  copyPositionsToTrigger(startingPosition, layedOutNodes, templateTrigger);
}

/**
 *
 * Try to fix overlapping nodes in the graph
 *
 * Algorithm:
 * 	1. get the subgraph that includes the given node. There should not be any collision in this subgraph (because we always layout them out when adding new nodes)
 * 	2. test if the rest of the nodes collide with the subgraph
 * 	3. move the nodes if there's a collison
 * 	4. It's possible that after movement, we create new collisions. Maybe an acceptable limitation? since it's unlikely to happen in trigger editor
 *
 * @param noMoveStep the step (and downstream) that we don't want to move in this process
 * @param outputGraph
 */
function fixCollisions(
  noMoveStep: TriggerStepCreate,
  outputGraph: TriggerStepCreate[]
) {
  const stepsNoMove = collectDownstreamSteps(noMoveStep, outputGraph);
  const stepIdsNoMoveSet = new Set(stepsNoMove.map((s) => s.id));
  const otherSteps = outputGraph.filter(
    (step) => !stepIdsNoMoveSet.has(step.id)
  );

  const noMoveNodes = createNodesFromStepsWithPosition(stepsNoMove);
  const otherNodes = createNodesFromStepsWithPosition(otherSteps);

  const idStepMap = getIdStepMap(outputGraph);
  const getNodeBounds = (node: TypedNode) => {
    invariant(
      typeof node.width === "number" && typeof node.height === "number"
    ),
      `Node doesn't have width/height! ${node.id}`;
    return {
      left: node.position.x,
      right: node.width + node.position.x - 1,
      top: node.position.y,
      bottom: node.position.y + node.height,
    };
  };

  // Push nodes away if they overlap with any of the noMoveNodes
  otherNodes.forEach((node) => {
    for (const noMoveNode of noMoveNodes) {
      if (isOverlap(node, noMoveNode)) {
        const boundsNode = getNodeBounds(node);
        const boundsNoMove = getNodeBounds(noMoveNode);
        const gap = 10;
        const offsetX =
          boundsNoMove.left <= boundsNode.left
            ? boundsNoMove.right - boundsNode.left + gap
            : boundsNoMove.left - boundsNode.right - gap;
        switch (node.type) {
          case "step":
          case "if-else":
            {
              const step = idStepMap.get(node.id);
              invariant(
                typeof step?.configs?.$$editorPosition?.x === "number",
                `Step doesn't have position information! ${step?.id}`
              );
              step.configs.$$editorPosition.x += offsetX;
            }
            break;
          case "condition":
            {
              {
                const step = idStepMap.get(node.data.stepId);
                invariant(
                  typeof step?.configs?.$$editorPositionWhenNode?.x ===
                    "number",
                  `Step doesn't have position information! ${step?.id}`
                );
                step.configs.$$editorPositionWhenNode.x += offsetX;
              }
            }
            break;
          default:
            break;
        }
      }
    }
  });
}

export function layoutAfterAddition(
  newStepIds: string[],
  allSteps: TriggerStepCreate[]
) {
  return produce(allSteps, (draftSteps) => {
    const firstNewStep = draftSteps.find((s) => newStepIds[0] === s.id);
    invariant(firstNewStep, `Can't find the first new step ${newStepIds[0]}`);
    const roots = getRoots(firstNewStep, draftSteps);
    const layoutStartingPoint = roots[0];

    layoutDownstream(layoutStartingPoint, draftSteps);
    fixCollisions(layoutStartingPoint, draftSteps);
  });
}

/**
 * Sometimes we can have some steps that don't have positions. E.g. after `triggerEditorReducer.fixTransitionsBaseOnDeletedSteps` runs, it might add placeholders that don't have positions. We'll get an error that's thrown from React Flow -- a bit difficult to track from the stack trace.
 *
 * This function fixes that by adding positions on those steps.
 *
 * @param outputSteps The steps to update
 */
function fixNoPositionSteps(outputSteps: TriggerStepCreate[]) {
  outputSteps.forEach((step) => {
    if (!step.configs?.$$editorPosition) {
      const firstParent = outputSteps.find((potentialParent) =>
        potentialParent.transitions.some(
          (t) =>
            t.targetStepId === step.id ||
            potentialParent.if?.elseStepId === step.id
        )
      );
      if (firstParent) {
        const transitionIdx = firstParent.transitions.findIndex(
          (t) => t.targetStepId === step.id
        );
        const transition = firstParent.transitions[transitionIdx];
        const hasWhen = Boolean(transition?.when);
        const position = hasWhen
          ? firstParent.configs?.$$editorPositionWhenNode ??
            firstParent.configs?.$$editorPosition // It's possible that a step on a when branch but its parent doesn't have an implicit conditional node. I.e. send_multiple_select
          : firstParent.configs?.$$editorPosition;
        invariant(
          position,
          `Parent (${firstParent.id}) doesn't have position info either!`
        );
        step.configs = {
          ...step.configs,
          $$editorPosition: {
            x: position.x,
            y: position.y + nodeSizes["step"].h,
          },
        };
      }
    }
  });
}

/**
 *
 * Lay out the steps after some steps are deleted.
 *
 * Algorithm:
 *
 *  1. Initialize set `noMove` to be the set of all nodes that are above the deleted nodes, set `toMove` to be the rest of nodes
 *  2. Remove any steps in `toMove` that would collide with steps in `noMove` if they are moved
 *  3. Move up steps that are in set `toMove`
 *
 * @param deletedSteps
 * @param graphAfterDeletion
 * @param newStepsIds
 * @returns an array of steps with positions updated
 */
export function layoutAfterDeletion(
  deletedSteps: TriggerStepCreate[],
  graphAfterDeletion: TriggerStepCreate[]
) {
  return produce(graphAfterDeletion, (draftGraph) => {
    fixNoPositionSteps(draftGraph);
    // Figure out topStep and bottomStep, and the height of the deleted block.
    const deletedStepsSorted = [...deletedSteps];
    const getY = (step: TriggerStepCreate) =>
      step.configs?.$$editorPosition?.y ??
      step.configs?.$$editorPositionWhenNode?.y ?? // Sometimes $$editorPosition is undefined but $$editorPositionWhenNode is defined. E.g. in case of send_captcha, the idle step is hidden but its when node is still visible.
      0;
    const getBottom = (step: TriggerStepCreate) => {
      return Math.max(
        (step.configs?.$$editorPositionWhenNode?.y ?? 0) +
          nodeSizes["condition"].h,
        getY(step) + nodeSizes["step"].h
      );
    };
    deletedStepsSorted.sort((s1, s2) => getY(s1) - getY(s2));
    const topStep = deletedStepsSorted[0];
    const bottomStep = deletedStepsSorted[deletedStepsSorted.length - 1];
    const height = getBottom(bottomStep) - getY(topStep) - 40; // Substract a small number to compensate the gap between steps. Otherwise the last placeholder of the second multi-select will fail to move up. See _stories DeleteMultiSelectInSuccessionDebug.

    // Collect the list of steps that are above the deleted bottomStep
    const aboveStepIds = draftGraph.reduce((set, step) => {
      invariant(
        step.configs?.$$editorPosition,
        `${step.id} doesn't have position info`
      );
      const aboveDeletedStep =
        step.configs.$$editorPosition.y <= getY(bottomStep);
      const whenNodeAboveDeletedStep =
        step.configs.$$editorPositionWhenNode &&
        step.configs.$$editorPositionWhenNode.y <= getY(bottomStep);
      if (aboveDeletedStep || whenNodeAboveDeletedStep) {
        set.add(step.id);
      }
      return set;
    }, new Set<string>());

    // Initialize two sets: noMove and toMove
    const noMoveStepIds = aboveStepIds;
    const toMoveStepIds = new Set(
      draftGraph.filter((s) => !noMoveStepIds.has(s.id)).map((s) => s.id)
    );

    // Remove steps from toMoveStepIds until it doesn't collide with any steps in noMoveStepIds. This is a O(n^3) algo, can we find a more efficient solution?
    let noCollisionStepCount = 0;
    while (noCollisionStepCount < toMoveStepIds.size) {
      const stepIdsToCheck = [...toMoveStepIds];
      for (const toMoveId of stepIdsToCheck) {
        if (
          willCollide(draftGraph, toMoveId, noMoveStepIds, {
            x: 0,
            y: -height,
          })
        ) {
          noMoveStepIds.add(toMoveId);
          toMoveStepIds.delete(toMoveId);
          noCollisionStepCount = 0;
          break;
        } else {
          noCollisionStepCount++;
        }
      }
    }

    // Finally, actually move the steps up!
    draftGraph.forEach((step) => {
      invariant(
        step.configs?.$$editorPosition,
        `${step.id} doesn't have position info`
      );
      if (toMoveStepIds.has(step.id)) {
        step.configs.$$editorPosition.y -= height;
        if (step.configs.$$editorPositionWhenNode) {
          step.configs.$$editorPositionWhenNode.y -= height;
        }
      }
    });

    // Sort the steps by their y positions. This is to make sure that the top step is recognized as the entry to prevent erroneous layout as reported in this ticket: https://app.shortcut.com/sedimentiq/story/3835/bug-deleting-a-step-after-a-trigger-moves-the-trigger-randomly-to-another-step
    draftGraph.sort((step1, step2) =>
      step1.isEntry
        ? 1
        : step2.isEntry
        ? -1
        : (step1.configs?.$$editorPosition?.y ?? 0) -
          (step2.configs?.$$editorPosition?.y ?? 0)
    );
  });
}

/**
 * Lay out the steps after one of them is updated
 *
 * Currently we re-layout the updated step and all its downstream steps.
 *
 * @param updatedStepId
 * @param allSteps
 * @returns an array of updated steps
 */
export function layoutAfterUpdate(
  updatedStepId: string,
  allSteps: TriggerStepCreate[]
) {
  const steps = produce(allSteps, (draftSteps) => {
    const updatedStep = allSteps.find((s) => s.id === updatedStepId);
    invariant(updatedStep, `Failed to find updated step: ${updatedStepId}`);
    const roots = getRoots(updatedStep, draftSteps);
    const entry = getEntryStep(draftSteps);
    const isEntryARoot = roots.findIndex((s) => s.id === entry.id) >= 0;
    const layoutStartingPoint = isEntryARoot ? entry : roots[0] ?? updatedStep;
    layoutDownstream(layoutStartingPoint, draftSteps);
    fixCollisions(layoutStartingPoint, draftSteps);
  });
  /* 
      For some reason some config steps are returning Proxy revokable objects, which causes the immer produce to fail.
      so this is a workaround to bypass this issue.
      We need to check perfomance issues down the line.
    */
  return JSON.parse(JSON.stringify(steps));
}
